import { useState, useEffect } from 'react';
import classNames from 'classnames';

import './TimeField.scss';

const isNull = a => a === null;
const isNumber = str => /^\d+$/.test(str);
const toNumber = str => parseInt(str, 10);
const isGreaterThan = n => str => toNumber(str) > n;
const isLessThan = n => str => toNumber(str) < n;
const toPaddedString = num => num.toString().padStart(2, '0');

const convertTo24Hour = (hour, period = 'am') => {
	const n = hour + (period === 'pm' ? 1 : 0) * 12;
	if (n === 24) {
		return 12;
	}
	if (n === 12) {
		return 0;
	}
	return n;
};

const convert24HourTo12Hour = hour => {
	if (hour === 0) return 12;
	return hour > 12 ? hour - 12 : hour;
};

export default function TimeField({
	id: initialId,
	name,
	label,
	isRequired,
	placeholder,
	errorMessage = false,

	// controlled
	value: timeValue = '',
	onChange,
}) {
	const [isFocused, setIsFocused] = useState(false);

	const handleFakeFocus = () => {
		setIsFocused(true);
	};

	const periodOptions = ['am', 'pm'];
	const [hours = '', mins = ''] = timeValue?.split(':')?.filter(isNumber)?.map(toNumber) || [
		'',
		'',
	];
	const period = periodOptions[Math.floor(hours / 12) % 2];

	const [values, setValues] = useState({
		hours: convert24HourTo12Hour(hours), // "" | 1...12
		mins,
		period, // "am" | "pm"
	});

	const valueIn24Hour =
		values.hours !== '' && values.mins !== ''
			? [convertTo24Hour(values.hours, values.period), values.mins].map(toPaddedString).join(':')
			: '';

	useEffect(() => {
		onChange({ target: { value: valueIn24Hour, id: initialId }, type: 'change' });
	}, [valueIn24Hour]);

	const handleChange = evt => {
		const { name, value } = evt.target;

		let nextValue = null;

		if (value === '') {
			nextValue = value;
		}

		if (name === 'period') {
			const matches = periodOptions.filter(period => period[0] === value[value.length - 1]);
			nextValue = matches[0] || values.period;
		} else if (isNumber(value)) {
			const valueSliced = value.slice(-2);
			const valueAsNumber = parseInt(valueSliced, 10);

			if (name === 'hours' && isGreaterThan(0)(valueAsNumber)) {
				if (isLessThan(13)(valueAsNumber)) {
					nextValue = valueAsNumber;
				} else {
					nextValue = parseInt(value.slice(-1), 10);
				}
			}

			if (name === 'mins' && isGreaterThan(-1)(valueAsNumber)) {
				if (isLessThan(60)(valueAsNumber)) {
					nextValue = valueAsNumber;
				} else {
					nextValue = parseInt(value.slice(-1), 10);
				}
			}
		}

		if (isNull(nextValue)) {
			return;
		}

		setValues(currentValues => {
			return {
				...currentValues,
				[name]: nextValue,
			};
		});
	};

	const paddedHour = values.hours !== '' ? toPaddedString(values.hours) : '';
	const displayHour = paddedHour === '00' ? '12' : paddedHour;
	const displayMins = values.mins !== '' ? toPaddedString(values.mins) : '';

	const id = initialId || `field--${name}`;

	const placeholderToDisplay = isRequired ? (placeholder || label) + '*' : placeholder || label;

	const hasError =
		(typeof errorMessage === 'boolean' && errorMessage === true) || errorMessage.length > 0;

	const showPlaceholder = !isFocused && values?.hours === '';

	return (
		<div className="TextInput formElement formElement-text ValidatedForm-Field">
			<div
				className={classNames('timeInputContainer TextInput-field formElement-field', {
					'formElement-field--filled': timeValue,
					'formElement-field--invalid fs-error-copy':
						hasError && !isFocused && values?.hours === '',
				})}
				onClick={handleFakeFocus}
				onFocus={handleFakeFocus}
				tabIndex="0"
				id={showPlaceholder ? id : `${id}__container`}
			>
				{showPlaceholder ? (
					placeholderToDisplay
				) : (
					<>
						<input
							name="hours"
							minLength="2"
							maxLength="3"
							placeholder="--"
							value={displayHour}
							onChange={handleChange}
							autoComplete="off"
							required={isRequired}
							id={id}
						/>
						:
						<input
							name="mins"
							minLength="2"
							maxLength="3"
							placeholder="--"
							value={displayMins}
							onChange={handleChange}
							required={isRequired}
							autoComplete="off"
						/>
						<input
							name="period"
							minLength="2"
							maxLength="3"
							value={values.period.toUpperCase()}
							onChange={handleChange}
							autoComplete="off"
							required={isRequired}
							style={{ width: '30px' }}
						/>
					</>
				)}
				<label
					className={classNames('formElement-label ty-c4 floatingLabel', {
						'floatingLabel--showing': isFocused || values?.hours !== '',
						'formElement-label--invalid fs-error-copy': hasError,
					})}
					htmlFor={id}
				>
					{isRequired ? label + '*' : label}
				</label>
				{errorMessage && (
					<span className="formElement-message fs-error-copy" role="alert">
						{errorMessage}
					</span>
				)}
			</div>
		</div>
	);
}
